import { useMutation } from '@tanstack/react-query';
import { useState, type SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import {
  challengeSecurityKeyMutation,
  challengeTOTPMutation,
  confirmChallengeTOTPMutation,
  createCredential,
  isCeremonyCancelled,
  isWebAuthnAvailable,
  type MFAChallenge,
  type SessionWithRecoveryCodes,
} from '@rootsight/shared';
import { Alert, Button, ErrorNotice, Spinner, Stack, TextField } from '@rootsight/ui';

import { api } from '../../api';

import { TOTPSetup } from './TOTPSetup';

interface EnrollStepProps {
  challenge: MFAChallenge;
  onSession: (session: SessionWithRecoveryCodes) => void;
}

/**
 * EnrollStep sets up the authenticator app the organization's policy
 * requires, in the middle of the sign-in: the secret as a QR code and as
 * text, then the first code it shows, which opens the session.
 */
export function EnrollStep({ challenge, onSession }: EnrollStepProps) {
  const { t } = useTranslation(['auth', 'common']);
  const [code, setCode] = useState('');
  const begin = useMutation(challengeTOTPMutation(api));
  const confirm = useMutation({ ...confirmChallengeTOTPMutation(api), onSuccess: onSession });
  const key = useMutation({
    ...challengeSecurityKeyMutation(api, createCredential),
    onSuccess: onSession,
  });
  const offerKey = challenge.methods.includes('webauthn') && isWebAuthnAvailable();

  const enrollment = begin.data;
  const submit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (!enrollment) return;
    confirm.mutate({ mfaToken: challenge.mfaToken, factorId: enrollment.factorId, code });
  };

  if (!enrollment) {
    return (
      <Stack gap={4}>
        <p>{t('login.enroll.required')}</p>
        {begin.isError && <ErrorNotice error={begin.error} />}
        {key.isError &&
          (isCeremonyCancelled(key.error) ? (
            <Alert tone="info">{t('login.mfa.keyCancelled')}</Alert>
          ) : (
            <ErrorNotice error={key.error} />
          ))}
        <Button
          variant="primary"
          size="lg"
          isPending={begin.isPending}
          onPress={() => begin.mutate({ mfaToken: challenge.mfaToken })}
        >
          {t('login.enroll.start')}
        </Button>
        {offerKey && (
          <Button
            size="lg"
            isPending={key.isPending}
            onPress={() => key.mutate({ mfaToken: challenge.mfaToken })}
          >
            {t('login.enroll.startKey')}
          </Button>
        )}
        {begin.isPending && <Spinner />}
      </Stack>
    );
  }
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap={4}>
        <TOTPSetup enrollment={enrollment} />
        {confirm.isError && <ErrorNotice error={confirm.error} />}
        <TextField
          label={t('login.mfa.code')}
          name="code"
          value={code}
          onChange={setCode}
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={8}
          isRequired
        />
        <Button type="submit" variant="primary" size="lg" isPending={confirm.isPending}>
          {t('login.enroll.confirm')}
        </Button>
      </Stack>
    </form>
  );
}
